<template>
  <div class="fcp-panel rec-anr">
    <!-- Title | counts | controls. The two sides share the leftover width equally, so the counts
         sit in the middle of the panel rather than just between the title and the controls. -->
    <div class="rec-anr__header d-flex flex-nowrap align-items-center">
      <div class="rec-anr__header-side d-flex align-items-center">
        <span class="rec-anr__title">Account Name Reconciliation</span>
      </div>
      <!-- Filter chips, one per count, in its Status column colour (red to fix, yellow waiting,
           green matched): click one to list only those rows, click it again to go back. Stays up
           while the list reloads, so it doesn't blink. -->
      <span v-if="hasLoaded && !loadError" class="rec-anr__summary">
        <button
          v-for="item in statusCounts"
          :key="item.key"
          type="button"
          class="rec-anr__count"
          :class="[`rec-anr__count--${item.tone}`, { 'rec-anr__count--active': statusFilter === item.key }]"
          :aria-pressed="statusFilter === item.key ? 'true' : 'false'"
          :title="statusFilter === item.key ? 'Show all again' : 'Show only these rows'"
          :disabled="loading"
          @click="setStatusFilter(item.key)"
        >
          {{ item.count }} {{ item.label }}
        </button>
      </span>
      <div class="rec-anr__header-side d-flex align-items-center justify-content-end">
        <input
          v-model="search"
          type="text"
          class="form-control rec-anr__search"
          placeholder="Search key, UMR or Account Name"
        />
        <!-- Same edit bar as Variance Transaction Details' edit mode: only while rows are
             selected - "N row(s) selected", Save, Discard. -->
        <div v-if="selectedCount" class="rec-anr__edit-bar ml-3">
          <span class="rec-anr__edit-count mr-2">{{ selectedCount }} row(s) selected</span>
          <button
            type="button"
            class="btn btn-primary btn-sm mr-2 rec-anr__edit-save"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
          <button
            type="button"
            class="btn btn-link btn-sm p-0 rec-anr__edit-discard"
            :disabled="saving"
            @click="discard"
          >
            Discard
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="rec-anr__message"><b-spinner small class="mr-2"></b-spinner>Loading Account Names…</div>
    <div v-else-if="loadError" class="rec-anr__message text-danger">{{ loadError }}</div>
    <div v-else-if="!visibleRows.length" class="rec-anr__message text-muted">{{ emptyMessage }}</div>
    <div v-else class="rec-anr__table-wrap">
      <table class="table table-sm mb-0 rec-anr__table">
        <thead>
          <tr>
            <th class="rec-anr__check">
              <input
                type="checkbox"
                :checked="allFixableSelected"
                :disabled="!fixableRows.length"
                title="Select every row with a new Account Name"
                @change="toggleAll"
              />
            </th>
            <!-- Resizable like Variance Transaction Details: drag the divider on a header's right
                 edge (same markup, styles and drag logic as RecProductionReport's). -->
            <th v-for="column in COLUMNS" :key="column.key">
              <div class="rec-anr__th-resizer" :style="columnStyle(column.key)">
                <span>{{ column.label }}</span>
                <button
                  v-if="column.key === 'status'"
                  id="rec-anr-status-legend"
                  type="button"
                  class="rec-anr__legend-toggle"
                  aria-label="What the statuses mean"
                  title="What the statuses mean"
                >
                  <b-icon icon="info-circle"></b-icon>
                </button>
              </div>
              <span class="rec-anr__col-resize-handle" @mousedown="startColumnResize($event, column.key)"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.key_id" :class="{ 'rec-anr__row--selected': selected[row.key_id] }">
            <td class="rec-anr__check">
              <input
                type="checkbox"
                :checked="!!selected[row.key_id]"
                :disabled="!isFixable(row)"
                title="Pick or type a new Account Name first"
                @change="toggle(row)"
              />
            </td>
            <td>
              <div class="rec-anr__td-resizer" :style="columnStyle('limeiq_key')" :title="row.limeiq_key">
                {{ row.limeiq_key }}
              </div>
            </td>
            <td>
              <div class="rec-anr__td-resizer" :style="columnStyle('umr')">{{ row.umr }}</div>
            </td>
            <td>
              <div class="rec-anr__td-resizer" :style="columnStyle('account_name')" :title="row.account_name">
                {{ row.account_name || '—' }}
              </div>
            </td>
            <td>
              <!-- Its option list is position: fixed, so this cell's overflow: hidden never clips it. -->
              <div class="rec-anr__td-resizer" :style="columnStyle('cashbook_name')">
                <rec-account-name-select
                  :value="pending[row.key_id]"
                  :options="row.options"
                  :disabled="saving"
                  @input="setPending(row, $event)"
                />
              </div>
            </td>
            <td>
              <div class="rec-anr__td-resizer" :style="columnStyle('status')">
                <template v-if="row.status === 'matched'">
                  <b-icon icon="check-circle-fill" variant="success" class="mr-1"></b-icon>Matched
                </template>
                <template v-else-if="row.status === 'no_cashbook'">
                  <b-icon icon="exclamation-circle-fill" class="mr-1 rec-anr__waiting-icon"></b-icon>Cash book not received
                </template>
                <template v-else>
                  <b-icon icon="x-circle-fill" variant="danger" class="mr-1"></b-icon>
                  {{ row.status === 'no_account_name' ? 'No Account Name' : 'Not in cash book' }}
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Opened by the ⓘ next to the Status header; closes on any click elsewhere. -->
      <b-popover
        target="rec-anr-status-legend"
        triggers="click blur"
        placement="bottom"
        boundary="window"
        custom-class="rec-anr-legend-popover"
      >
        <div class="rec-anr__legend">
          <div class="rec-anr__legend-title">What the statuses mean</div>
          <dl class="mb-0">
            <dt><b-icon icon="x-circle-fill" variant="danger" class="mr-1"></b-icon>No Account Name</dt>
            <dd>
              The key has no BDX Match Account Name, so its BDX rows are left out of the report. Pick or type a
              name.
            </dd>
            <dt><b-icon icon="x-circle-fill" variant="danger" class="mr-1"></b-icon>Not in cash book</dt>
            <dd>
              The key's name isn't one of the cash book's names for its UMR, so its BDX and cash book rows never
              pair up. Pick the cash book's spelling from the list.
            </dd>
            <dt>
              <b-icon icon="exclamation-circle-fill" class="mr-1 rec-anr__waiting-icon"></b-icon>Cash book not
              received
            </dt>
            <dd>
              No cash book has arrived for this UMR yet, so there's nothing to compare the name against. Usually
              nothing to do until it arrives.
            </dd>
            <dt><b-icon icon="check-circle-fill" variant="success" class="mr-1"></b-icon>Matched</dt>
            <dd>The key's name is one of the cash book's names for its UMR, so its BDX and cash book rows pair up.</dd>
          </dl>
          <div class="rec-anr__legend-note">
            Names count as the same when they differ only in spaces or upper/lower case.
          </div>
        </div>
      </b-popover>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from '@vue/composition-api'
import { useToast } from '@composables'
import { ReconciliationService, LincolnGlobalUMRService } from '@services'
import RecAccountNameSelect from '@components/RecAccountNameSelect'

// Resizable columns (after the select-all checkbox). The three long ones start at a width rather
// than sizing to their longest value, which would stretch the table - same idea as VTD's seeded
// 'variance:claim' width. Dragging overrides any of them.
const COLUMNS = [
  { key: 'limeiq_key', label: 'LimeIQ Key' },
  { key: 'umr', label: 'UMR' },
  { key: 'account_name', label: 'BDX Match Account Name' },
  { key: 'cashbook_name', label: 'Cash Book Account Name' },
  { key: 'status', label: 'Status' }
]
const DEFAULT_COLUMN_WIDTHS = { limeiq_key: 260, account_name: 260, cashbook_name: 280 }

// The header counts' filters: which row statuses each one lists. With none picked, the panel lists
// to fix + waiting - matched keys only when "matched" is picked.
const STATUS_GROUPS = {
  outstanding: ['no_account_name', 'mismatch'],
  no_cashbook: ['no_cashbook'],
  matched: ['matched']
}
const DEFAULT_STATUSES = [...STATUS_GROUPS.outstanding, ...STATUS_GROUPS.no_cashbook]

const apiError = (err, fallback) => {
  const data = err && err.response && err.response.data
  return (data && (typeof data.data === 'string' ? data.data : data.error || data.detail)) || fallback
}

// LIQ-2571: inline "Account Name Reconciliation" panel on the rec page. Lists the rec-enabled
// keys in the caller's scope (superusers: all; underwriters: keys their company is on) with each
// key's Account Name status, lets the user pick or type the right name per row, and saves the
// selected rows in one request through Lincoln, which sets only account_name and keeps the
// history. The report itself picks a fix up once the recompute worker has rebuilt that account.
//
// All of this panel's state lives here, not on RecProductionReport: a reactive change there
// re-renders its Variance table's every row (see RecInlineOptionsMenu's comment).
export default defineComponent({
  name: 'rec-account-name-reconciliation-panel',
  components: {
    RecAccountNameSelect
  },
  emits: ['counted', 'saved'],
  setup(_props, { emit }) {
    const { showToast } = useToast()

    const loading = ref(false)
    const loadError = ref('')
    const saving = ref(false)
    const rows = ref([])
    const outstandingCount = ref(0)
    const noCashbookCount = ref(0)
    // From the server: matched keys are always counted, but only fetched once "matched" is clicked.
    const matchedCount = ref(0)
    const search = ref('')
    const hasLoaded = ref(false)

    // Header count filter (a STATUS_GROUPS key, or null for the default view). Matched keys aren't
    // fetched until "matched" is first picked; after that they stay loaded, so switching between
    // the counts filters what's already here - no reload, and no typed names dropped.
    const statusFilter = ref(null)
    const includeMatched = ref(false)

    const statusCounts = computed(() => [
      { key: 'outstanding', count: outstandingCount.value, label: 'to fix', tone: 'danger' },
      { key: 'no_cashbook', count: noCashbookCount.value, label: 'waiting for CB', tone: 'waiting' },
      { key: 'matched', count: matchedCount.value, label: 'matched', tone: 'success' }
    ])

    // Column resize - same as RecProductionReport's columnWidths/columnStyle/startColumnResize
    // (Variance/BDX/Cashbook): a width only clips once a column has one; drag sets it, min 40px.
    const columnWidths = ref({ ...DEFAULT_COLUMN_WIDTHS })

    const columnStyle = (key) => {
      const width = columnWidths.value[key]
      return width
        ? { width: `${width}px`, maxWidth: `${width}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        : {}
    }

    const startColumnResize = (event, key) => {
      event.preventDefault()
      const th = event.target.closest('th')
      const startX = event.clientX
      const startWidth = th ? th.offsetWidth : 100

      const onMouseMove = (moveEvent) => {
        columnWidths.value = { ...columnWidths.value, [key]: Math.max(40, startWidth + (moveEvent.clientX - startX)) }
      }
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }
    // key_id -> typed/picked Account Name, and key_id -> selected for saving.
    const pending = ref({})
    const selected = ref({})

    const load = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const response = await ReconciliationService.getAccountNameReconciliation({
          includeMatched: includeMatched.value
        })
        const data = (response && response.data) || {}
        rows.value = data.rows || []
        outstandingCount.value = data.outstanding_count || 0
        noCashbookCount.value = data.no_cashbook_count || 0
        matchedCount.value = data.matched_count || 0
        hasLoaded.value = true
        emit('counted', outstandingCount.value)
        // Drop edits for keys no longer listed (e.g. a fix just saved, now matched and hidden).
        const listed = new Set(rows.value.map((row) => row.key_id))
        pending.value = Object.fromEntries(Object.entries(pending.value).filter(([id]) => listed.has(Number(id))))
        selected.value = Object.fromEntries(Object.entries(selected.value).filter(([id]) => listed.has(Number(id))))
      } catch (err) {
        loadError.value = apiError(err, 'Failed to load Account Names.')
      } finally {
        loading.value = false
      }
    }

    const setStatusFilter = async (key) => {
      statusFilter.value = statusFilter.value === key ? null : key
      if (statusFilter.value === 'matched' && !includeMatched.value) {
        includeMatched.value = true
        await load()
      }
    }

    const visibleRows = computed(() => {
      const statuses = statusFilter.value ? STATUS_GROUPS[statusFilter.value] : DEFAULT_STATUSES
      const query = search.value.trim().toLowerCase()
      return rows.value.filter(
        (row) =>
          statuses.includes(row.status) &&
          (!query ||
            [row.limeiq_key, row.umr, row.account_name].some((value) => (value || '').toLowerCase().includes(query)))
      )
    })

    const emptyMessage = computed(() => {
      if (search.value.trim()) return 'No keys match your search.'
      if (statusFilter.value) return 'No keys in this group.'
      return 'Nothing to reconcile - every key in your scope matches its cash book.'
    })

    // A row can be saved once it has a non-blank name that differs from the current one.
    const isFixable = (row) => {
      const name = (pending.value[row.key_id] || '').trim()
      return !!name && name !== (row.account_name || '').trim()
    }

    const fixableRows = computed(() => visibleRows.value.filter(isFixable))
    const selectedCount = computed(() => Object.values(selected.value).filter(Boolean).length)
    const allFixableSelected = computed(
      () => fixableRows.value.length > 0 && fixableRows.value.every((row) => selected.value[row.key_id])
    )

    const setPending = (row, value) => {
      pending.value = { ...pending.value, [row.key_id]: value }
      // Choosing a new name selects the row; clearing it (or choosing the current name) unselects it.
      selected.value = { ...selected.value, [row.key_id]: isFixable(row) }
    }

    const toggle = (row) => {
      if (!isFixable(row)) return
      selected.value = { ...selected.value, [row.key_id]: !selected.value[row.key_id] }
    }

    const toggleAll = () => {
      const select = !allFixableSelected.value
      const next = { ...selected.value }
      fixableRows.value.forEach((row) => {
        next[row.key_id] = select
      })
      selected.value = next
    }

    const save = async () => {
      const updates = rows.value
        .filter((row) => selected.value[row.key_id] && isFixable(row))
        .map((row) => ({ id: row.key_id, account_name: pending.value[row.key_id].trim() }))
      if (!updates.length) return

      saving.value = true
      try {
        const response = await LincolnGlobalUMRService.recUpdateAccountNames(updates)
        const saved = (response && response.data && response.data.data && response.data.data.updated) || []
        showToast(
          `Saved ${saved.length} Account Name${saved.length === 1 ? '' : 's'}. ` +
            'The report reflects the change within a minute or two.'
        )
        const savedIds = new Set(updates.map((update) => update.id))
        pending.value = Object.fromEntries(Object.entries(pending.value).filter(([id]) => !savedIds.has(Number(id))))
        selected.value = {}
        emit('saved')
        await load()
      } catch (err) {
        showToast(apiError(err, 'Failed to save Account Names. Nothing was changed.'), 'error')
      } finally {
        saving.value = false
      }
    }

    // Same as Variance Transaction Details' Discard: drop every unsaved name and unselect all.
    const discard = () => {
      pending.value = {}
      selected.value = {}
    }

    onMounted(load)

    return {
      loading,
      loadError,
      saving,
      rows,
      outstandingCount,
      noCashbookCount,
      matchedCount,
      hasLoaded,
      statusFilter,
      statusCounts,
      setStatusFilter,
      emptyMessage,
      search,
      pending,
      selected,
      visibleRows,
      fixableRows,
      selectedCount,
      allFixableSelected,
      isFixable,
      load,
      setPending,
      toggle,
      toggleAll,
      save,
      discard,
      COLUMNS,
      columnStyle,
      startColumnResize
    }
  }
})
</script>

<style scoped>
/* Sizes match RecProductionReport.scss: the page's base font is tiny (.fcp-report 0.4rem) and
   its own rules don't reach inside this component, so everything here is set explicitly -
   title like "Variance Transaction Details", table like the Variance/BDX/Cashbook grids. */
/* Same box as VTD's title row (.fcp-panel__title: 0.8rem text at the normal 1.5 line height, then
   0.35rem below): a fixed 1.2rem-high line rather than one that grows to fit its tallest item.
   Save is taller than that, and - centred, overflow visible - spills evenly above and below,
   reaching the panel's top edge and the table header with no white band, exactly as VTD's does. */
/* The page decides this panel's height (RecProductionReport.scss's .fcp-anr-panel); the header
   keeps its size and the table fills the rest, scrolling inside it. */
.rec-anr {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.rec-anr__header {
  flex-shrink: 0;
  gap: 0.5rem;
  height: 1.2rem;
  margin-bottom: 0.35rem;
  white-space: nowrap;
}

.rec-anr__title {
  font-weight: 700;
  color: #b06a1e;
  font-size: 0.8rem;
}

.rec-anr__header-side {
  flex: 1 1 0;
}

.rec-anr__summary {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
}

/* The counts are filter chips - a tinted pill in the count's colour, darker tint on hover, solid
   when picked - the usual look for something you click to filter. Red and green are the theme's
   $danger/$success (assets/sass/theme/custom/_variables.scss), the same as the Status column's
   ✖/✔ icons. */
.rec-anr__count {
  border: 1px solid;
  border-radius: 999px;
  padding: 0 0.5rem;
  font: inherit;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.rec-anr__count:focus {
  outline: none;
}

.rec-anr__count:focus-visible {
  box-shadow: 0 0 0 2px rgba(176, 106, 30, 0.35);
}

.rec-anr__count:disabled {
  cursor: wait;
  opacity: 0.7;
}

.rec-anr__count--danger {
  color: #f5365c;
  border-color: rgba(245, 54, 92, 0.45);
  background: rgba(245, 54, 92, 0.08);
}

.rec-anr__count--danger:hover {
  background: rgba(245, 54, 92, 0.18);
}

.rec-anr__count--danger.rec-anr__count--active {
  color: #fff;
  border-color: #f5365c;
  background: #f5365c;
}

/* Yellow, not variant="warning": this theme maps $warning to a red-orange (#fb6340) that reads as
   another red next to "to fix" - same reason as RecProductionReport.scss's .fcp-icon-pending,
   whose true yellow (#ffd600) the Status icon and the picked chip use. The chip's text is a deeper
   yellow, since #ffd600 is too faint to read as small text; the picked chip keeps dark text, as
   white on yellow is unreadable. */
.rec-anr__count--waiting {
  color: #b38f00;
  border-color: rgba(212, 168, 0, 0.55);
  background: rgba(255, 214, 0, 0.12);
}

.rec-anr__count--waiting:hover {
  background: rgba(255, 214, 0, 0.28);
}

.rec-anr__count--waiting.rec-anr__count--active {
  color: #2c2c2c;
  border-color: #d4a800;
  background: #ffd600;
}

.rec-anr__count--success {
  color: #48b900;
  border-color: rgba(72, 185, 0, 0.45);
  background: rgba(72, 185, 0, 0.08);
}

.rec-anr__count--success:hover {
  background: rgba(72, 185, 0, 0.18);
}

.rec-anr__count--success.rec-anr__count--active {
  color: #fff;
  border-color: #48b900;
  background: #48b900;
}

.rec-anr__waiting-icon {
  color: #ffd600;
}

/* Short enough to sit inside the 1.2rem header line. Darker border, text and placeholder than
   the theme's, which nearly vanish on the panel's white. */
.rec-anr__search {
  width: 240px;
  height: auto;
  font-size: 0.65rem;
  line-height: 1.2;
  padding: 0.1rem 0.4rem;
  border: 1px solid #adb5bd;
  color: #2c2c2c;
}

.rec-anr__search::placeholder {
  color: #6c757d;
}

/* Copied from RecProductionReport.scss's .fcp-variance-edit-bar (Variance Transaction Details'
   edit mode), including its override of the page's tiny global .btn size. */
.rec-anr__edit-bar {
  display: flex;
  align-items: center;
}

.rec-anr__edit-bar button.btn {
  font-size: 0.8rem;
  line-height: 1.2;
}

/* VTD's edit bar sits inside .fcp-panel__title, so its count text inherits the title's bold
   orange - set here explicitly, since this bar isn't inside a title. */
.rec-anr__edit-count {
  font-size: 0.7rem;
  font-weight: 700;
  color: #b06a1e;
}

/* Flat, like VTD's Save: the theme gives both a drop shadow, but on VTD the table header right
   under the button hides it, while here a small gap below the header leaves it showing - which
   made this Save look raised and taller. */
.rec-anr__edit-bar .rec-anr__edit-save,
.rec-anr__edit-bar .rec-anr__edit-save:hover,
.rec-anr__edit-bar .rec-anr__edit-save:focus,
.rec-anr__edit-bar .rec-anr__edit-save:active {
  box-shadow: none;
}

.rec-anr__edit-bar .rec-anr__edit-save {
  padding: 0.35rem 1rem;
}

.rec-anr__edit-bar .rec-anr__edit-discard {
  color: #e2574c !important;
}

.rec-anr__edit-bar .rec-anr__edit-discard:hover,
.rec-anr__edit-bar .rec-anr__edit-discard:focus {
  color: #c94435 !important;
}

.rec-anr__message {
  padding: 0.5rem 0.25rem;
  font-size: 0.7rem;
}

.rec-anr__table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  /* Same thin scrollbar as the REC UMRs list (.fcp-umr-list). */
  scrollbar-width: thin;
}

.rec-anr__table-wrap::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.rec-anr__table-wrap::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 3px;
}

/* Copied from RecProductionReport.scss's .umr-table (the REC UMRs table) - same "table table-sm"
   classes underneath, so the theme gives both tables the same cell font sizes and padding; the
   page's own .umr-table rules are scoped to the page and don't reach this component. Its header
   cells pad their .fcp-th-resizer (2px 12px 2px 2px); these headers have none, so the th does. */
.rec-anr__table {
  width: 100%;
}

.rec-anr__table td {
  white-space: nowrap;
  vertical-align: middle;
}

.rec-anr__table th,
.rec-anr__table td {
  border-right: 1px solid #ced7d9;
}

.rec-anr__table th:last-child,
.rec-anr__table td:last-child {
  border-right: none;
}

.rec-anr__table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #d7dde0;
  color: #2c2c2c;
  font-weight: 700;
  border-top: none;
  border-bottom: none;
  border-left: none;
  white-space: nowrap;
  text-transform: none;
  letter-spacing: normal;
  padding: 0;
}

/* Column resize - copied from RecProductionReport.scss's .fcp-th-resizer / .fcp-td-resizer /
   .fcp-col-resize-handle. The sticky th is the handle's positioning context. */
.rec-anr__th-resizer {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 40px;
  max-width: 480px;
  white-space: nowrap;
  padding: 2px 12px 2px 2px;
  height: 100%;
  box-sizing: border-box;
}

.rec-anr__td-resizer {
  display: block;
}

/* ⓘ next to the Status header - opens the status legend. */
.rec-anr__legend-toggle {
  background: none;
  border: 0;
  padding: 0;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
}

.rec-anr__legend-toggle:hover,
.rec-anr__legend-toggle:focus {
  color: #b06a1e;
  outline: none;
}

/* The legend's content is this component's own markup (b-popover's slot), so these scoped rules
   reach it even though the popover box itself is drawn at <body>. */
.rec-anr__legend {
  font-size: 0.7rem;
  color: #2c2c2c;
}

.rec-anr__legend-title {
  font-weight: 700;
  color: #b06a1e;
  margin-bottom: 0.35rem;
}

.rec-anr__legend dt {
  font-weight: 700;
  white-space: nowrap;
}

.rec-anr__legend dd {
  margin: 0 0 0.4rem 1.1rem;
}

.rec-anr__legend-note {
  margin-top: 0.2rem;
  padding-top: 0.35rem;
  border-top: 1px solid #e3e8ec;
  color: #6c757d;
}

.rec-anr__col-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 1;
}

.rec-anr__col-resize-handle:hover,
.rec-anr__col-resize-handle:active {
  background: rgba(108, 117, 125, 0.25);
}

.rec-anr__table tbody tr:nth-of-type(odd) {
  background-color: rgba(222, 226, 230, 0.3);
}

.rec-anr__table tbody tr:nth-of-type(even) {
  background-color: #ffffff;
}

/* The Cash Book Account Name box looks like VTD's Submitter Comments box - copied from
   RecProductionReport.scss's .fcp-variance-edit-input (same form-control form-control-sm
   underneath): orange left edge, 0.62rem text, same padding and height. */
.rec-anr__table ::v-deep .form-control {
  border-left: 2px solid #d98a2e;
  font-size: 0.62rem;
  padding: 0.1rem 0.35rem;
  height: calc(1.4em + 0.25rem + 2px);
}

.rec-anr__check {
  width: 32px;
  text-align: center;
}

/* A row's checkbox stays disabled until a new name is picked for it (and select-all until any row
   has one) - faded well past the browser's own barely-lighter disabled look, so it's obvious which
   rows can't be selected yet. */
.rec-anr__check input[type='checkbox']:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Beats the striping above (one more class of specificity). */
.rec-anr__table tbody tr.rec-anr__row--selected {
  background-color: #fff4e5;
}
</style>

<style>
/* The status legend's popover box (custom-class on its b-popover): drawn at <body>, outside this
   component, so not scoped. Wider than Bootstrap's 276px default so the descriptions don't wrap
   into a tall narrow column. */
.rec-anr-legend-popover {
  max-width: 340px;
}
</style>
